import React from 'react'
import { renderToString } from 'react-dom/server'
import { Marker } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getAllSensorValues } from '../../redux/reducers'
import wordwrap from 'wordwrapjs'
import styled from 'styled-components'

const NAME_CHARS_PER_LINE = 19

export const MARKER_MODES = {
  DETAILED: 'detailed',
  COMPACT: 'compact',
  DOT: 'dot',
}

const DETAILED_ZOOM = 17
const COMPACT_ZOOM = 15

const FieldModuleLabel = ({ show, line1, line2 }) => {
  return show ? (
    <text
      x="75"
      y="16"
      fill="white"
      stroke="black"
      strokeWidth="3"
      paintOrder="stroke"
      fontSize="16px"
      fontFamily="'Asap',sans-serif"
    >
      {line1 && (
        <tspan x="50%" textAnchor="middle">
          {line1}
        </tspan>
      )}
      {line2 && (
        <tspan x="50%" textAnchor="middle" dy="15">
          {line2}
        </tspan>
      )}
    </text>
  ) : null
}

const getWrappedName = ({ name, mapDisplayName }) =>
  mapDisplayName || name
    ? wordwrap.lines(mapDisplayName || name, {
        width: NAME_CHARS_PER_LINE,
      })
    : []

export const getMarkerAnchor = (module, displayMode) => {
  if (displayMode === MARKER_MODES.DOT) {
    return new window.google.maps.Point(10, 10)
  }

  if (displayMode === MARKER_MODES.DETAILED) {
    const [, nameLine2] = getWrappedName(module)
    return new window.google.maps.Point(75, (nameLine2 ? 40 : 20) + 36)
  }

  return new window.google.maps.Point(15, 36)
}

const SensorValue = ({ sensorValue, index }) => {
  const { t } = useTranslation()
  const { currentValue, name, unit } = sensorValue
  const { value, timestamp } = currentValue
  const unitTranslated = t(`attributes:sensorValue:unitOptions:${unit}`)
  // first element has no dy offset
  const dy = index == 0 ? '0' : '15'

  return (
    sensorValue && (
      <tspan x="0" textAnchor="start" dy={dy}>
        {name} <tspan className="value">{value || '-'}</tspan>{' '}
        <tspan className="unit">{unitTranslated}</tspan>
      </tspan>
    )
  )
}

const SensorValues = ({ sensorValues, offsetY }) => {
  const y = (60 + Number(offsetY)).toString()
  return (
    <>
      <style type="text/css">{`
        .value{fill: white;font-weight: bold;}
        .unit{fill: white;font-weight: bold;}
      `}</style>
      <text
        x="25"
        y={y}
        fill="white"
        stroke="black"
        strokeWidth="3"
        paintOrder="stroke"
        fontSize="14px"
        fontFamily="'Asap',sans-serif"
      >
        {(sensorValues || []).map((sensorValue, index) => (
          <SensorValue sensorValue={sensorValue} index={index} key={index} />
        ))}
      </text>
    </>
  )
}

export const getMarkerDisplayMode = (module, denseModuleIds, currentZoom) => {
  if (denseModuleIds.has(module.id)) {
    return MARKER_MODES.DOT
  }

  if (currentZoom >= DETAILED_ZOOM) {
    return MARKER_MODES.DETAILED
  }

  if (currentZoom >= COMPACT_ZOOM) {
    return MARKER_MODES.COMPACT
  }

  return MARKER_MODES.DOT
}

export const ModuleMarkerIcon = ({
  hasOpenValve,
  hasUnreadError,
  hasSuspendedValve,
  selected,
  name,
  mapDisplayName,
  showFieldModuleLabel,
  showSensorValues,
  sensorValues,
  displayMode = MARKER_MODES.DETAILED,
}) => {
  const wrappedName = getWrappedName({ name, mapDisplayName })
  const [nameLine1, nameLine2, ..._unusedRest] = wrappedName

  // we need more space for label if it has two lines
  const markerOffsetY = nameLine2 ? '40' : '20'

  if (displayMode === MARKER_MODES.DOT) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="10"
          cy="10"
          r="7"
          fill={hasOpenValve ? '#1647F3' : 'white'}
          fillOpacity={selected ? 0.95 : 0.7}
          stroke={hasUnreadError ? '#FF0000' : 'white'}
          strokeWidth={selected ? '3' : '2'}
        />
        {hasSuspendedValve && (
          <>
            <path d="M6 6L14 14" stroke="white" strokeWidth="1.5" />
            <path d="M14 6L6 14" stroke="white" strokeWidth="1.5" />
          </>
        )}
      </svg>
    )
  }

  const detailed = displayMode === MARKER_MODES.DETAILED
  const width = detailed && showFieldModuleLabel ? '150' : '30'
  const height = detailed && showFieldModuleLabel ? '150' : '37'
  const viewBox = `0 0 ${width} ${height}`
  return (
    <svg
      width={width}
      height={height}
      viewBox={viewBox}
      fill="green"
      xmlns="http://www.w3.org/2000/svg"
    >
      <FieldModuleLabel
        show={detailed && showFieldModuleLabel}
        line1={nameLine1}
        line2={nameLine2}
      />

      {detailed && showSensorValues && (
        <SensorValues sensorValues={sensorValues} offsetY={markerOffsetY} />
      )}

      <svg
        width="30"
        height="37"
        x={detailed && showFieldModuleLabel ? '60' : '0'}
        y={detailed && showFieldModuleLabel ? markerOffsetY : '0'}
        viewBox="0 0 30 37"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15 36C15 36 3.5493 23.336 1.81137 19.2455C1.28457 17.8091 1 16.2601 1 14.6465C1 7.11332 7.24368 1 14.9441 1H15.0525C22.7563 1 29 7.11332 29 14.6465C29 16.2601 28.7154 17.8091 28.1886 19.2472C26.4507 23.336 15 36 15 36Z"
          fill={hasOpenValve ? '#1647F3' : 'white'}
          fillOpacity={selected ? 0.9 : 0.35}
        />
        <path
          d="M5.5 5L24.5 24"
          stroke={hasSuspendedValve ? 'white' : 'none'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M24.5 5L6.26001 24"
          stroke={hasSuspendedValve ? 'white' : 'none'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15 36C15 36 3.5493 23.336 1.81137 19.2455C1.28457 17.8091 1 16.2601 1 14.6465C1 7.11332 7.24368 1 14.9441 1H15.0525C22.7563 1 29 7.11332 29 14.6465C29 16.2601 28.7154 17.8091 28.1886 19.2472C26.4507 23.336 15 36 15 36Z"
          stroke={hasUnreadError ? '#FF0000' : 'white'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </svg>
  )
}

const FieldModule = ({ module, selected, displayMode, ...props }) => {
  const filterByModule = ({ wateringModuleId, showOnMap }) =>
    wateringModuleId === module.id && showOnMap
  const sensorValues = useSelector((state) =>
    getAllSensorValues(state, filterByModule)
  )
  const { lat, lng } = module
  const showSensorValues = displayMode === MARKER_MODES.DETAILED
  const icon = renderToString(
    <ModuleMarkerIcon
      {...module}
      selected={selected}
      showFieldModuleLabel={displayMode === MARKER_MODES.DETAILED}
      displayMode={displayMode}
      showSensorValues={showSensorValues}
      sensorValues={sensorValues}
    />
  )
  return (
    <Marker
      {...props}
      icon={{
        url: `data:image/svg+xml;charset=UTF-8,${window.encodeURIComponent(
          icon
        )}`,
        anchor: getMarkerAnchor(module, displayMode),
      }}
      position={{ lat, lng }}
    />
  )
}

export default FieldModule
